import { create } from 'zustand';

import { WIDGET_FILTERS } from '@/config/filters.config';
import { DASHBOARD_WIDGETS } from '@/config/widgets.config';

/**
 * TDEV-266 Phase 6 — widget-filter store (persistence-free).
 *
 * Server-backed persistence runs through `SettingsSync` (see
 * `apps/dashboard/web/src/components/SettingsSync.tsx`). The zustand
 * `persist` middleware is intentionally removed so the only persistence
 * surface is the `admin_settings` row + the TanStack `EffectiveSettings`
 * query, not localStorage.
 *
 * Public surface ({ filters, setWidgetFilter, resetWidgetFilters }) MUST
 * stay intact — 21 consumer files import this hook (plan.md:1218,
 * Decision #14 LOCK).
 */
function buildWidgetDefaults(): Record<string, Record<string, string>> {
  const defaults: Record<string, Record<string, string>> = {};
  for (const widget of DASHBOARD_WIDGETS) {
    const widgetFilterIds = widget.filters?.widget;
    if (!widgetFilterIds?.length) continue;

    const widgetDefaults: Record<string, string> = {};
    for (const filterId of widgetFilterIds) {
      const filterConfig = WIDGET_FILTERS[filterId];
      if (filterConfig?.defaultValue) {
        widgetDefaults[filterId] = filterConfig.defaultValue;
      }
    }
    if (Object.keys(widgetDefaults).length > 0) {
      defaults[widget.id] = widgetDefaults;
    }
  }
  return defaults;
}

const widgetDefaults = buildWidgetDefaults();

interface WidgetFilterState {
  filters: Record<string, Record<string, string>>;
  setWidgetFilter: (widgetId: string, filterId: string, value: string) => void;
  resetWidgetFilters: (widgetId: string) => void;
}

export const useWidgetFilterStore = create<WidgetFilterState>()((set) => ({
  filters: structuredClone(widgetDefaults),
  setWidgetFilter: (widgetId, filterId, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [widgetId]: {
          ...state.filters[widgetId],
          [filterId]: value,
        },
      },
    })),
  resetWidgetFilters: (widgetId) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [widgetId]: widgetDefaults[widgetId] ? { ...widgetDefaults[widgetId] } : {},
      },
    })),
}));
